import { message } from "antd";
import markdownit from "markdown-it";
import { saveAs } from "file-saver";
import { v4 as uuidv4 } from "uuid";
import { apiRequest, API_ENDPOINTS } from "../config/api";
import { getImageUrlByScene } from "./urlUtils";

// 定义颜色常量
const TEXT_COLOR_BLACK = "#111";

/**
 * 初始化markdown-it渲染器
 * 启用HTML、自动换行、链接转换和排版功能
 */
const md = markdownit({
    html: true,
    breaks: true,
    linkify: true,
    typographer: true,
});

/**
 * PDF生成工具函数
 * 使用直接将HTML渲染为Canvas再转为PDF的方式，适合中文内容
 */
const PdfGenerator = {
    /**
     * 根据Markdown文本创建并下载PDF
     * @param markdownText Markdown格式的文本内容
     */
    async generatePdf(markdownText: string): Promise<void> {

        try {
            // 显示加载消息
            message.loading({
                content: "正在生成PDF文档...",
                key: "pdfGeneration",
            });

            console.log("开始生成PDF...");

            // 使用新版图片处理方式生成PDF
            await generateEnhancedPdf(markdownText);
            return Promise.resolve();
        } catch (error) {
            console.error("生成PDF失败:", error);
            // 显示错误消息
            message.error({
                content: "生成PDF失败，请重试",
                key: "pdfGeneration",
            });
            return Promise.reject(error);
        }
    },

    /**
     * 将内容下载为图片而非PDF
     * @param markdownText Markdown格式的文本内容
     */
    async downloadAsImage(markdownText: string): Promise<void> {
        try {
            // 显示加载消息
            message.loading({
                content: "正在生成图片...",
                key: "imageGeneration",
            });

            // 创建DOM容器
            const container = await prepareContainer(markdownText);

            // 导入html2canvas库
            const html2canvas = (await import("html2canvas")).default;

            // 预处理容器中的图片，确保跨域设置正确
            await preloadImages(container);

            console.log("container", container.innerHTML);

            // 将HTML转换为图片并下载
            await downHtml2canvas(container, html2canvas, "福利助手回复");

            // 显示成功消息
            message.success({
                content: "图片生成成功，已开始下载",
                key: "imageGeneration",
                duration: 2,
            });

            // 清理DOM
            if (document.body.contains(container)) {
                document.body.removeChild(container);
            }

            return Promise.resolve();
        } catch (error) {
            console.error("生成图片失败:", error);
            // 显示错误消息
            message.error({
                content: "生成图片失败，请重试",
                key: "imageGeneration",
            });
            return Promise.reject(error);
        }
    },
};

/**
 * 预处理和创建HTML容器
 * @param markdownText Markdown文本
 * @returns 创建好的DOM容器
 */
async function prepareContainer(markdownText: string): Promise<HTMLElement> {
    // 创建DOM容器
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "0";
    // A4纸宽度（595pt ≈ 595px at 72dpi）
    container.style.width = "595px";
    document.body.appendChild(container);

    // 获取当前日期时间
    const currentDate = new Date().toLocaleDateString("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

    // 渲染Markdown
    const htmlContent = md.render(markdownText);

    const companyDesc =
        `中智关爱通（上海）科技股份有限公司（证券代码∶871282）是中国国际技术智力合作集团有限公司旗下福利事务数字化服务平台，是国务院国资委"科改示范企业"、"创建世界一流专精特新示范企业"和国有重点企业管理标杆创建行动"标杆企业"。中智关爱通业务涵盖福利、津贴、健康等在内的员工多科目预算的分发管理、数据归集及财务分析；同时依托上下游资源整合能力，为组织内的成员提供衣食住行等高频强粘度场景的个性化品质服务。`;


    const tips = "如需关爱通福利顾问为您定制方案，您可以扫描下方二维码";
    const img = `<img src="${getImageUrlByScene()}" alt="关爱通福利顾问" style="width: 180px; height: 180px; display: block; margin-left: auto; margin-right: auto;">`;


    container.style.width = "595px"; // A4宽度
    container.style.paddingTop = "20px";
    container.style.paddingBottom = "20px";
    container.style.paddingLeft = "40px";
    container.style.paddingRight = "40px";
    container.style.backgroundColor = "#fff";
    container.style.fontSize = "16px";
    container.style.color = TEXT_COLOR_BLACK;

    container.innerHTML = `
        <div style="width: 100%; 
        padding-top: 20px; 
        padding-bottom: 20px; 
        padding-left: 20px; 
        padding-right: 20px; 
        background-color: #fff; 
        color: ${TEXT_COLOR_BLACK};
        font-family: 'Microsoft YaHei', 'SimSun', sans-serif;">
            <div style="font-size: 16px; line-height: 1.8; color: ${TEXT_COLOR_BLACK};">${htmlContent}</div>
			<br/>
            <div style="font-size: 14px; color: #666; margin-bottom: 20px; text-align: center;">本方案由AI生成，仅供参考</div>
			<div style="font-size: 14px; color: #666; margin-bottom: 20px; text-align: center;">${tips}</div>
			<div>${img}</div>
            <div style="font-size: 14px; color: #666; margin-top: 20px; margin-bottom: 20px; text-align: center;">${companyDesc}</div>
        </div>
    `;

    // 处理h1样式
    container.querySelectorAll("h1").forEach((h1) => {
        const h1Elem = h1 as HTMLElement;
        h1Elem.style.fontSize = "28px";
        h1Elem.style.color = '#ff6600';
        h1Elem.style.marginTop = "12px";
        h1Elem.style.marginBottom = "12px";
        h1Elem.style.textAlign = "center";
    });

    // 处理h2样式
    container.querySelectorAll("h2").forEach((h2) => {
        const h2Elem = h2 as HTMLElement;
        h2Elem.style.fontSize = "22px";
        h2Elem.style.color = TEXT_COLOR_BLACK;
        h2Elem.style.marginTop = "12px";
        h2Elem.style.marginBottom = "12px";
        h2Elem.style.fontWeight = "bold";
    });

    // 处理h3样式
    container.querySelectorAll("h3").forEach((h3) => {
        const h3Elem = h3 as HTMLElement;
        h3Elem.style.fontSize = "18px";
        h3Elem.style.color = TEXT_COLOR_BLACK;
        h3Elem.style.marginBottom = "6px";
        h3Elem.style.fontWeight = "bold";
    });

    // 处理所有段落样式
    container.querySelectorAll("p").forEach((p) => {
        const pElem = p as HTMLElement;
        pElem.style.color = TEXT_COLOR_BLACK;
        pElem.style.lineHeight = "1.8";
        pElem.style.marginBottom = "10px";
    });

    // 处理列表样式
    container.querySelectorAll("ul, ol").forEach((list) => {
        const listElem = list as HTMLElement;
        listElem.style.color = TEXT_COLOR_BLACK;
        listElem.style.paddingLeft = "20px";
    });

    container.querySelectorAll("li").forEach((li) => {
        const liElem = li as HTMLElement;
        liElem.style.color = TEXT_COLOR_BLACK;
        liElem.style.lineHeight = "1.6";
        liElem.style.marginBottom = "5px";
    });

    // 处理强调文本样式
    container.querySelectorAll("strong, b").forEach((strong) => {
        const strongElem = strong as HTMLElement;
        strongElem.style.color = TEXT_COLOR_BLACK;
    });

    container.querySelectorAll("em, i").forEach((em) => {
        const emElem = em as HTMLElement;
        emElem.style.color = TEXT_COLOR_BLACK;
    });

    // 处理表格样式
    container.querySelectorAll("table").forEach((table) => {
        const tableElem = table as HTMLElement;
        tableElem.style.width = "100%";
        tableElem.style.borderCollapse = "collapse";
        tableElem.style.margin = "15px 0";
        tableElem.style.fontSize = "16px";
        tableElem.style.color = TEXT_COLOR_BLACK;

        table.querySelectorAll("th, td").forEach((cell) => {
            const cellElem = cell as HTMLElement;
            cellElem.style.border = "1px solid #ddd";
            cellElem.style.padding = "8px 12px";
            cellElem.style.textAlign = "left";
            cellElem.style.color = TEXT_COLOR_BLACK; // 确保表格内容为黑色
        });

        table.querySelectorAll("th").forEach((header) => {
            const headerElem = header as HTMLElement;
            headerElem.style.backgroundColor = "#f2f2f2";
            headerElem.style.fontWeight = "bold";
            headerElem.style.color = TEXT_COLOR_BLACK; // 确保表头为黑色
        });
    });

    // 处理引用块样式
    container.querySelectorAll("blockquote").forEach((quote) => {
        const quoteElem = quote as HTMLElement;
        quoteElem.style.color = '#666';
        quoteElem.style.borderLeft = "4px solid #ddd";
        quoteElem.style.paddingLeft = "16px";
        quoteElem.style.margin = "16px 0";
    });

    // 处理代码块样式
    container.querySelectorAll("code").forEach((code) => {
        const codeElem = code as HTMLElement;
        codeElem.style.color = '#666';
        codeElem.style.backgroundColor = "#f5f5f5";
        codeElem.style.padding = "2px 4px";
        codeElem.style.borderRadius = "3px";
    });

    container.querySelectorAll("pre").forEach((pre) => {
        const preElem = pre as HTMLElement;
        preElem.style.color = '#666';
        preElem.style.backgroundColor = "#f5f5f5";
        preElem.style.padding = "12px";
        preElem.style.borderRadius = "5px";
        preElem.style.overflow = "auto";
    });

    // 处理链接样式
    container.querySelectorAll("a").forEach((link) => {
        const linkElem = link as HTMLElement;
        linkElem.style.color = "#00f";
        linkElem.style.textDecoration = "underline";
    });

    // 最后统一设置所有文本节点为黑色（兜底处理）
    const allElements = container.querySelectorAll("*");
    allElements.forEach((element) => {
        const elem = element as HTMLElement;
        if (!elem.style.color) {
            elem.style.color = TEXT_COLOR_BLACK;
        }
    });

    return container;
}

/**
 * 预加载容器内的所有图片，确保能正确渲染
 * @param container DOM容器
 */
async function preloadImages(
    container: HTMLElement
): Promise<HTMLCanvasElement> {
    const images = Array.from(container.querySelectorAll("img"));

    // 如果没有图片，直接返回
    if (images.length > 0) {
        console.log(`准备预加载 ${images.length} 张图片`);

        // 为所有图片设置crossOrigin属性
        images.forEach(async (img) => {
            // 设置img的id为uuid
            img.id = uuidv4();
            // 存储图片原始URL
            // 调用api post的接口 /ps-assistant-gateway-api/get_image_base64 使用post方法 传入图片url 返回base64
            // 使用集中管理的API端点和请求函数
            const data = await apiRequest(API_ENDPOINTS.GET_IMAGE_BASE64, {
                method: "POST",
                body: JSON.stringify({ url: img.src }),
            });
            img.src = `data:image/png;base64,${data.image_base64}`;
            console.log(img.src);
            // 设置样式确保图片显示正确
            img.style.maxWidth = "100%";
            img.style.height = "auto";
        });

        // 等待所有图片加载完成或超时
        const timeout = 6000; // 5秒超时
        const loadImages = images.map((img) => {
            return new Promise<void>((resolve) => {
                if (img.complete) {
                    resolve();
                    return;
                }

                img.onload = () => resolve();
                img.onerror = () => {
                    console.warn(`图片加载失败: ${img.src}`);
                    resolve(); // 即使失败也继续
                };

                // 添加超时处理
                setTimeout(() => {
                    if (!img.complete) {
                        console.warn(`图片加载超时: ${img.src}`);
                        resolve();
                    }
                }, timeout);
            });
        });

        await Promise.all(loadImages);
        console.log("所有图片预加载完成");
    }
    // 导入所需库
    const html2canvas = await import("html2canvas");

    // 给予额外时间确保DOM渲染完成
    await new Promise((resolve) => setTimeout(resolve, 5000));
    console.log("开始生成Canvas...");
    const canvas = await html2canvas.default(container, {
        scale: 2.0, // 提高清晰度
        useCORS: true, // 允许跨域
        allowTaint: true, // 允许图片污染Canvas，提高成功率
        backgroundColor: "#FFFFFF",
        logging: true,
        imageTimeout: 5000, // 增加图片加载超时时间
        onclone: (clonedDoc: Document) => {
            return Promise.resolve();
        },
    });
    console.log("Canvas生成完成");
    return canvas;
}

/**
 * 将HTML元素下载为图片
 * @param el 要转换的DOM元素
 * @param html2canvasLib html2canvas库实例
 * @param name 下载的文件名
 */
async function downHtml2canvas(
    el: HTMLElement,
    html2canvasLib: any,
    name = "未命名"
): Promise<void> {
    try {
        const canvas = await html2canvasLib(el, {
            scale: 2.0, // 提高清晰度
            allowTaint: true, // 允许图片污染Canvas，提高成功率
            useCORS: true, // 允许跨域
            logging: false,
            backgroundColor: "#FFFFFF",
            imageTimeout: 5000, // 增加图片加载超时时间
            onclone: (clonedDoc: Document) => {
                // 确保克隆的文档也应用crossOrigin设置
                clonedDoc.querySelectorAll("img").forEach((img) => {
                    console.log("img.src", img.src);
                });
                return Promise.resolve();
            },
        });

        // 创建临时链接并下载
        const src = canvas.toDataURL("image/png", 1);
        const url = src.replace(
            /^data:image\/[^;]/,
            "data:application/octet-stream"
        );
        const a = document.createElement("a");
        a.download = `${name}-${new Date().getTime()}.png`;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        a.remove();

        return Promise.resolve();
    } catch (error) {
        console.error("下载图片失败:", error);
        throw error;
    }
}

/**
 * 增强版PDF生成方法 - 使用图片处理逻辑但输出PDF
 */
async function generateEnhancedPdf(markdownText: string): Promise<void> {
    // 创建DOM容器
    let container: HTMLElement | null = null;

    try {
        // 准备容器
        container = await prepareContainer(markdownText);

        // 预处理容器中的图片，确保跨域设置正确
        const canvas = await preloadImages(container);

        // 将HTML转换为Canvas

        // 获取Canvas尺寸
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        const jspdf = await import("jspdf");
        // 创建PDF - 使用A4纸张尺寸
        let pdf = new jspdf.jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4",
            compress: true,
        });

        // 关键设置：将分割线颜色设为透明
        pdf.setDrawColor(255, 255, 255); // 白色（或与背景色一致）
        // 移除页面边框
        pdf.setLineWidth(0);

        // 禁用页面边框
        pdf.setFillColor(255, 255, 255);
        pdf.setTextColor(0, 0, 0);

        // 获取A4页面的可用宽度和高度
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // 根据A4宽度和Canvas宽度计算缩放比例
        const scale = pageWidth / imgWidth;
        // 计算等比例缩放后的高度
        const scaledHeight = imgHeight * scale;

        // 创建PDF - 使用A4纸张尺寸
        pdf = new jspdf.jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: [pageWidth, scaledHeight],
            compress: true,
        });

        // 处理多页PDF的情况
        let heightLeft = scaledHeight;
        let position = 0;
        let page = 1;

        // 将canvas转换为图像数据
        const imgData = canvas.toDataURL("image/jpeg", 0.9);

        // 分页渲染逻辑
        let currentPosition = 0;

        // 添加第一页
        pdf.addImage({
            imageData: imgData,
            format: "JPEG",
            x: 0,
            y: currentPosition,
            width: pageWidth,
            height: scaledHeight,
            compression: "FAST",
            rotation: 0,
        });


        heightLeft -= pageHeight;

        // 生成文件名
        const timestamp = new Date().getTime();
        const filename = `福利助手回复-${timestamp}.pdf`;

        // 保存PDF
        const pdfOutput = pdf.output("blob");
        saveAs(pdfOutput, filename);

        // 显示成功消息
        message.success({
            content: "PDF生成成功",
            key: "pdfGeneration",
            duration: 3,
        });

        return Promise.resolve();
    } catch (error) {
        console.error("生成PDF失败:", error);
        throw error;
    } finally {
        // 清理DOM
        if (container && document.body.contains(container)) {
            document.body.removeChild(container);
        }
    }
}

export { PdfGenerator };
